import { getAmountAndTermCalculator } from 'hooks/useApplicationSummary';
import { useCalculatorContext } from './CalculatorContext';
import { useCallback, useEffect } from 'react';
import CalculatorEvent from 'hooks/eventCollector/events/CalculatorEvent';
import classNames from 'classnames';
import Slider from '../Slider/Slider';
import styles from './Calculator.module.scss';
import useApplication from 'hooks/useApplication';
import useClient from 'hooks/useClient';
import useClientConstraints from 'hooks/useClientConstraints';
import useEventCollector from 'hooks/eventCollector/useEventCollector';

export const Form = ({ className }: { className?: string }) => {
  const {
    isLoading,
    amount,
    setAmount,
    days,
    setDays,
    onChange,
    hideDays,
    content,
  } = useCalculatorContext();
  const { client } = useClient();
  const { data: constraints } = useClientConstraints();
  const { data: application } = useApplication();
  const { track } = useEventCollector();

  const defaultFormValues = getAmountAndTermCalculator({
    constraints,
    application,
  });

  const handleChangeAmount = useCallback(
    (value: number) => {
      setAmount(value);
      onChange?.('amount', value);
      track(
        new CalculatorEvent({
          description: 'calculator amount change',
          value,
        }),
      );
    },
    [onChange],
  );

  const handleChangeDays = useCallback(
    (value: number) => {
      setDays(value);
      onChange?.('days', value);
      track(
        new CalculatorEvent({
          description: 'calculator days change',
          value,
        }),
      );
    },
    [onChange],
  );

  useEffect(() => {
    if (!constraints || !application) return;
    // give time to the calculator to replace the default value according to application and constraint
    setTimeout(() => {
      setAmount(defaultFormValues?.amount);
      setDays(defaultFormValues?.term);
    }, 100);
  }, [defaultFormValues?.amount, defaultFormValues?.term]);

  const clientLoanLimit = String((client?.loansCount || 0) + 1);

  return (
    <div
      className={classNames([
        styles.form,
        isLoading && styles.loading,
        className,
      ])}
      data-test="css-Edit-Request-Container"
    >
      {/* Amount */}
      <Slider
        className={styles.sliderElement}
        dataTest="amount"
        disabled={isLoading}
        loading={isLoading}
        max={
          Number(constraints?.loanLimits?.[clientLoanLimit]) ||
          constraints?.amountInterval?.max ||
          500
        }
        min={constraints?.amountInterval?.min || 100}
        step={constraints?.amountInterval?.step || 10}
        onChange={handleChangeAmount}
        units={content.amountUnit}
        value={amount}
      />
      {/* Days */}
      {!hideDays && (
        <>
          <Slider
            className={styles.sliderElement}
            dataTest="term"
            disabled={isLoading}
            loading={isLoading}
            max={constraints?.termInterval?.max || 30}
            min={constraints?.termInterval?.min || 7}
            step={constraints?.termInterval?.step || 1}
            onChange={handleChangeDays}
            units={content.daysUnit}
            value={days}
          />
        </>
      )}
    </div>
  );
};
